import React, { useState } from 'react';
import {
  Box, IconButton, Badge, Popper, Paper, ClickAwayListener, Typography, List,
  ListItemButton, ListItemText, Divider, Button, Chip, CircularProgress,
} from '@mui/material';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../contexts/NotificationContext';
import { brandColors, RADIUS, BASE_FONT_SIZE, brandCss } from '../../theme/appTheme';

const TYPE_COLORS: Record<string, { bg: string; color: string }> = {
  assignment: { bg: brandCss.green100, color: brandCss.primaryDark },
  mention: { bg: '#dbeafe', color: '#1d4ed8' },
  comment: { bg: '#fef3c7', color: '#b45309' },
  record: { bg: '#ede9fe', color: '#6d28d9' },
  tenant: { bg: '#fce7f3', color: '#be185d' },
  system: { bg: '#f1f5f9', color: brandColors.textMuted },
  security: { bg: '#fee2e2', color: '#b91c1c' },
};

const formatTime = (iso: string) => {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return d.toLocaleDateString();
};

export const NotificationCenter: React.FC = () => {
  const navigate = useNavigate();
  const {
    notifications, unreadCount, loading, markRead, markAllRead, remove, refresh,
  } = useNotifications();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const open = Boolean(anchor);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchor(e.currentTarget);
    refresh();
  };

  const handleClick = async (id: string, link?: string, read?: boolean) => {
    if (!read) await markRead(id);
    setAnchor(null);
    if (link) navigate(link);
  };

  return (
    <>
      <IconButton sx={{ color: brandColors.textMuted }} onClick={handleOpen} aria-label="Notifications">
        <Badge badgeContent={unreadCount} color="error" max={99}>
          {unreadCount > 0 ? <NotificationsActiveIcon /> : <NotificationsNoneIcon />}
        </Badge>
      </IconButton>

      <Popper open={open} anchorEl={anchor} placement="bottom-end" sx={{ zIndex: 1500, width: 380 }}>
        <ClickAwayListener onClickAway={() => setAnchor(null)}>
          <Paper
            elevation={12}
            sx={{
              mt: 1.5, borderRadius: `${RADIUS}px`, border: `1px solid ${brandColors.border}`,
              overflow: 'hidden', maxHeight: 480, display: 'flex', flexDirection: 'column',
            }}
          >
            <Box sx={{
              px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              bgcolor: brandCss.green50, borderBottom: `1px solid ${brandColors.border}`,
            }}>
              <Typography fontWeight={700} fontSize={BASE_FONT_SIZE}>Notifications</Typography>
              {unreadCount > 0 && (
                <Button size="small" startIcon={<DoneAllIcon />} onClick={markAllRead}
                  sx={{ fontSize: BASE_FONT_SIZE, textTransform: 'none' }}>
                  Mark all read
                </Button>
              )}
            </Box>

            {loading && !notifications.length ? (
              <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={28} sx={{ color: brandCss.primary }} />
              </Box>
            ) : notifications.length === 0 ? (
              <Box sx={{ py: 4, px: 2, textAlign: 'center' }}>
                <Typography fontSize={BASE_FONT_SIZE} color="textSecondary">No notifications yet</Typography>
              </Box>
            ) : (
              <List dense sx={{ overflowY: 'auto', flex: 1, py: 0 }}>
                {notifications.map((n, i) => {
                  const colors = TYPE_COLORS[n.type] || TYPE_COLORS.system;
                  return (
                    <React.Fragment key={n._id}>
                      {i > 0 && <Divider component="li" />}
                      <ListItemButton
                        alignItems="flex-start"
                        onClick={() => handleClick(n._id, n.link, n.read)}
                        sx={{
                          py: 1.5, px: 2, bgcolor: n.read ? 'transparent' : brandCss.green50,
                          '&:hover': { bgcolor: n.read ? brandColors.contentBg : brandCss.green100 },
                        }}
                      >
                        <ListItemText
                          primary={(
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography fontSize={BASE_FONT_SIZE} fontWeight={700} sx={{ flex: 1 }}>
                                {n.title}
                              </Typography>
                              {!n.read && (
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: brandCss.primary }} />
                              )}
                            </Box>
                          )}
                          secondary={(
                            <Box>
                              <Typography fontSize={BASE_FONT_SIZE} color="textSecondary" sx={{ mb: 0.75 }}>
                                {n.message}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip label={n.type.replace('_', ' ')} size="small"
                                  sx={{ height: 20, fontSize: BASE_FONT_SIZE - 1, bgcolor: colors.bg, color: colors.color, textTransform: 'capitalize' }} />
                                <Typography variant="caption" color="textSecondary">{formatTime(n.createdAt)}</Typography>
                                <IconButton
                                  size="small"
                                  sx={{ ml: 'auto' }}
                                  onClick={(e) => { e.stopPropagation(); remove(n._id); }}
                                  aria-label="Remove notification"
                                >
                                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Box>
                            </Box>
                          )}
                        />
                      </ListItemButton>
                    </React.Fragment>
                  );
                })}
              </List>
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
};
